/**
 * Locale/measured-count formatting (design.md §6 "tabular figures"/§5.1 item
 * 2). Pure functions — no rendering, no I/O — reused by `MetricCell` and the
 * execution status matrix so every table cell agrees on one number format.
 */

const NUMBER_FORMATTER = new Intl.NumberFormat("es-CO");

/**
 * Runtime guard against NaN/Infinity/-Infinity/a wrong runtime type reaching
 * a formatter (review fix, Jose Luis Arcila PR #95 follow-up). TypeScript's
 * `value: number` annotation is a compile-time claim only — a JSON-parsed
 * API response can still deliver `NaN`, `Infinity`, or (if some other bug
 * upstream produced it) any non-numeric value that survived past the type
 * system. `Intl.NumberFormat().format(NaN)` returns the literal string
 * `"NaN"` and `.format(Infinity)` returns `"∞"` — both would render as if
 * they were legitimate measurements. `Number.isFinite` alone is not enough:
 * unlike the global `isFinite`, it correctly does NOT coerce a non-number
 * type first, but it also does not verify the runtime type is `number` to
 * begin with — the explicit `typeof` check is what actually earns this
 * function's right to be called a genuine runtime guard, not merely a
 * redundant re-statement of the TS type.
 */
export function isValidCount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Locale-formatted integer, e.g. `1240` → `"1.240"` (es-CO thousands separator). */
export function formatCount(value: number): string {
  if (!isValidCount(value)) {
    throw new Error(`formatCount: value must be a finite number, received: ${String(value)}`);
  }
  return NUMBER_FORMATTER.format(value);
}

/**
 * design.md §5.1 item 2: "Zero is rendered as `0` with 'medido'" — a measured
 * zero (a real fact: nothing happened) must read differently from an
 * unavailable/blocked F2-01 cell, never as a bare, ambiguous `0`. A nonzero
 * measured value needs no such qualifier — it is unambiguous on its own.
 *
 * Every render call site (`MetricCell`, `ExecutionStatusMatrix`,
 * `AttentionLedger`) is expected to call `isValidCount` itself BEFORE
 * reaching this function, so a real UI never renders "Dato inválido" for
 * anything but a genuinely invalid value. The `value === 0` branch below
 * never delegates to `formatCount`, so this defensive throw is reached only
 * via the nonzero branch's call to `formatCount` — a deliberate single
 * source of truth for the backstop rather than duplicating the check here.
 */
export function formatMeasuredCount(value: number): string {
  return value === 0 ? "0 · medido" : formatCount(value);
}
